import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthAPI, clearStoredToken, getStoredToken, saveStoredToken } from "../Api/Api";
import { Capacitor } from '@capacitor/core';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => getStoredToken());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const savedToken = getStoredToken();
    if (!savedToken) {
      setToken(null);
      setUser(null);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setToken(savedToken);
    AuthAPI.me(savedToken)
      .then((data) => {
        localStorage.setItem('abdullah_cached_user', JSON.stringify(data.user));
        if (active) setUser(data.user);
      })
      .catch((error) => {
        const cached = localStorage.getItem('abdullah_cached_user');
        if (error.status === 401 || !cached || !Capacitor.isNativePlatform()) {
          clearStoredToken();
          if (active) { setToken(null); setUser(null); }
        } else if (active) {
          try { setUser(JSON.parse(cached)); } catch { setUser(null); }
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      clearStoredToken();
      localStorage.removeItem('abdullah_cached_user');
      setToken(null);
      setUser(null);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  const login = useCallback(async (credentials, rememberMe = false) => {
    const data = await AuthAPI.login({ ...credentials, remember_me: Boolean(rememberMe) });
    saveStoredToken(data.token, rememberMe);
    setToken(data.token);
    try {
      const profile = await AuthAPI.me(data.token);
      setUser(profile.user);
      localStorage.setItem('abdullah_cached_user', JSON.stringify(profile.user));
      return profile.user;
    } catch (error) {
      clearStoredToken();
      setToken(null);
      setUser(null);
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    clearStoredToken();
    localStorage.removeItem('abdullah_cached_user');
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      user,
      loading,
      isAuthenticated: Boolean(token && user),
      login,
      logout,
    }),
    [token, user, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider.");
  return context;
}
